import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Send, Plus, Search, Filter, Calendar as CalendarIcon, List,
  MoreVertical, CheckCircle2, Sparkles, MessageSquare, Settings as Cog,
  Instagram, Facebook, Twitter, Linkedin, ExternalLink, Image as ImageIcon,
  Clock, Trash2, Copy, FileText, ChevronDown, Check, Video, Play, Eye,
  Timer, Link2, Users, Palette, Megaphone, TrendingUp, DollarSign,
  Share2, ArrowUpRight, BarChart3, HelpCircle, Code, ShieldCheck, Flame
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";

// Top GHL Marketing Suite Tabs (Matching Screenshot)
const TOP_TABS = [
  { id: "social-planner", label: "Social Planner" },
  { id: "emails", label: "Emails" },
  { id: "snippets", label: "Snippets" },
  { id: "countdown", label: "Countdown Timers" },
  { id: "trigger-links", label: "Trigger Links" },
  { id: "affiliate", label: "Affiliate Manager", hasDropdown: true },
  { id: "brand-boards", label: "Brand Boards" },
  { id: "ad-manager", label: "Ad Manager" },
];

// Secondary Sub-tabs under Social Planner (Matching Screenshot)
const PLANNER_SUBTABS = [
  { id: "planner", label: "Planner" },
  { id: "content", label: "Content" },
  { id: "comments", label: "Comments" },
  { id: "statistics", label: "Statistics" },
  { id: "intelligence", label: "Social Intelligence", badge: "BETA" },
  { id: "settings", label: "Settings" },
];

const NetworkFacebook = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const NetworkInstagram = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
    <radialGradient id="ig-grad-com" cx="20%" cy="100%" r="150%">
      <stop offset="0%" stopColor="#fdf497"/>
      <stop offset="10%" stopColor="#fdf497"/>
      <stop offset="45%" stopColor="#fd5949"/>
      <stop offset="60%" stopColor="#d6249f"/>
      <stop offset="90%" stopColor="#285AEB"/>
    </radialGradient>
    <rect width="24" height="24" rx="6" fill="url(#ig-grad-com)"/>
    <circle cx="12" cy="12" r="4.2" stroke="#fff" strokeWidth="2"/>
    <circle cx="18" cy="6" r="1.2" fill="#fff"/>
  </svg>
);

const NetworkLinkedIn = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="#0A66C2">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
  </svg>
);

const NetworkTikTok = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.29 0 .58.04.85.12V9.38a6.34 6.34 0 0 0-.85-.06A6.34 6.34 0 0 0 3.14 15.66a6.34 6.34 0 0 0 9.17 5.61v-7.3a8.16 8.16 0 0 0 4.78 1.54v-3.41a4.85 4.85 0 0 1-2.5-.72 4.85 4.85 0 0 1-1.41-1.69v-.01z"/>
  </svg>
);

const NetworkCommunity = () => (
  <svg className="w-4 h-4 shrink-0 text-indigo-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const NetworkBluesky = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="#0085FF">
    <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.03-.274.064-.409.102-3.891 1.106-7.013 3.424-3.568 7.822 4.095 5.228 7.375.405 8.555-2.223 1.18 2.628 4.46 7.451 8.555 2.223 3.445-4.398.323-6.716-3.568-7.822-.135-.038-.271-.072-.409-.102.14.017.279.036.415.056 2.67.296 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.479 0-.688-.139-1.86-.902-2.203-.659-.3-1.664-.62-4.3 1.24C16.046 4.747 13.087 8.686 12 10.8z"/>
  </svg>
);

const NetworkThreads = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.186 24C5.454 24 0 18.618 0 12.012 0 5.406 5.454.024 12.186.024c6.732 0 12.186 5.382 12.186 11.988 0 6.606-5.454 11.988-12.186 11.988zm0-2.18c5.523 0 10-4.39 10-9.808 0-5.418-4.477-9.808-10-9.808-5.523 0-10 4.39-10 9.808 0 5.418 4.477 9.808 10 9.808z"/>
  </svg>
);

const NetworkPinterest = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="#E60023">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

const NetworkYouTube = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="#FF0000">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const COMMENT_NETWORKS = [
  { id: "facebook", name: "Facebook", icon: <NetworkFacebook /> },
  { id: "instagram", name: "Instagram", icon: <NetworkInstagram /> },
  { id: "linkedin", name: "LinkedIn", icon: <NetworkLinkedIn /> },
  { id: "tiktok", name: "TikTok", icon: <NetworkTikTok /> },
  { id: "community", name: "Community", icon: <NetworkCommunity /> },
  { id: "bluesky", name: "Bluesky", icon: <NetworkBluesky /> },
  { id: "threads", name: "Threads", icon: <NetworkThreads /> },
  { id: "pinterest", name: "Pinterest", icon: <NetworkPinterest /> },
  { id: "youtube", name: "YouTube", icon: <NetworkYouTube /> },
];

const PLATFORMS = [
  { id: "instagram", name: "Instagram", icon: Instagram, color: "#E1306C", bg: "bg-pink-50" },
  { id: "facebook", name: "Facebook", icon: Facebook, color: "#1877F2", bg: "bg-blue-50" },
  { id: "twitter", name: "Twitter / X", icon: Twitter, color: "#1DA1F2", bg: "bg-sky-50" },
  { id: "linkedin", name: "LinkedIn", icon: Linkedin, color: "#0A66C2", bg: "bg-indigo-50" },
];

export default function Marketing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const topTab = searchParams.get("tab") || "social-planner";
  const setTopTab = (id) => setSearchParams({ tab: id });
  const [subTab, setSubTab] = useState("planner");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // list | calendar
  const [dateStart, setDateStart] = useState("30 / 05 / 2026");
  const [dateEnd, setDateEnd] = useState("30 / 11 / 2026");

  // ── Comments Sub-Tab State (Matching GHL Screenshot) ──
  const [selectedSocialNetwork, setSelectedSocialNetwork] = useState("linkedin");
  const [selectedSocialAccount, setSelectedSocialAccount] = useState(null); // null = "No social account selected" matching user screenshot
  const [commentFilter, setCommentFilter] = useState("all");
  const [commentStartDate, setCommentStartDate] = useState("");
  const [commentEndDate, setCommentEndDate] = useState("");
  const [commentSearchWord, setCommentSearchWord] = useState("");
  const [commentReplyDrafts, setCommentReplyDrafts] = useState({});
  const [commentsList, setCommentsList] = useState([
    {
      id: "cm-1",
      network: "linkedin",
      authorName: "Sarah Jenkins",
      authorRole: "VP of Operations at Apex Culinary Group",
      authorAvatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&auto=format&fit=crop&q=80",
      time: "25m ago",
      postTitle: "5 platforms. One goal: Get noticed...",
      postThumb: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=120&auto=format&fit=crop&q=80",
      text: "The automated lead follow-up and review capture engine LaunchDeck built for our private dining room generated 44 five-star Google reviews in the first 30 days! Are you taking on new retainers for Q1?",
      sentiment: "lead",
      replies: [
        { author: "RealHUB Team", time: "10m ago", text: "Thank you so much Sarah! Yes, our Q1 onboarding is live. We just sent the strategy session link to your email." }
      ]
    },
    {
      id: "cm-2",
      network: "linkedin",
      authorName: "Marcus Vance",
      authorRole: "Managing Director at Skyline Real Estate",
      authorAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80",
      time: "1h ago",
      postTitle: "Results start with consistency. Wa...",
      postThumb: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=120&auto=format&fit=crop&q=80",
      text: "Is the client portal white-labeled under our domain or does it show LaunchDeck branding? We need custom CSS and single sign-on for our commercial investors.",
      sentiment: "question",
      replies: []
    },
    {
      id: "cm-3",
      network: "instagram",
      authorName: "Elena Rostova",
      authorRole: "@elena_luxury_travel",
      authorAvatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80",
      time: "2h ago",
      postTitle: "Want more people to find you onlin...",
      postThumb: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=120&auto=format&fit=crop&q=80",
      text: "Where can we book this tasting experience? The lighting in this photo from Colibri Studio is unbelievable! 😍",
      sentiment: "lead",
      replies: []
    },
    {
      id: "cm-4",
      network: "facebook",
      authorName: "David Miller",
      authorRole: "The Grand Haven Hotel",
      authorAvatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&auto=format&fit=crop&q=80",
      time: "3h ago",
      postTitle: "$1.7 Million. Zero cold calls...",
      postThumb: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=120&auto=format&fit=crop&q=80",
      text: "Can confirm these numbers. Since setting up the reputation automation and Google review triggers, our weekend brunch occupancy jumped to 98%.",
      sentiment: "positive",
      replies: []
    },
    {
      id: "cm-5",
      network: "youtube",
      authorName: "Alex Rivera",
      authorRole: "@alexriveraproductions",
      authorAvatar: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=120&auto=format&fit=crop&q=80",
      time: "5h ago",
      postTitle: "Google is for searching, but Yelp is...",
      postThumb: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=120&auto=format&fit=crop&q=80",
      text: "Great breakdown on local SEO citation accuracy. What cameras and color profile were used for the B-roll footage?",
      sentiment: "question",
      replies: []
    }
  ]);

  const handleSendCommentReply = (commentId) => {
    const draft = commentReplyDrafts[commentId]?.trim();
    if (!draft) {
      toast.error("Please enter a reply message");
      return;
    }
    setCommentsList(prev => prev.map(c => {
      if (c.id === commentId) {
        return {
          ...c,
          replies: [...c.replies, { author: "RealHUB Team", time: "Just now", text: draft }]
        };
      }
      return c;
    }));
    setCommentReplyDrafts(prev => ({ ...prev, [commentId]: "" }));
    toast.success("Reply published successfully!");
  };

  const handleAiDraftReply = (comment) => {
    let suggested = "";
    if (comment.sentiment === "lead") {
      suggested = `Hi ${comment.authorName.split(" ")[0]}! Thanks so much for reaching out. We would love to collaborate - I've just sent our VIP booking deck to your direct messages!`;
    } else if (comment.sentiment === "question") {
      suggested = `Great question, ${comment.authorName.split(" ")[0]}! Yes, our client portal and funnels are 100% white-labeled on your custom domain with zero external branding.`;
    } else {
      suggested = `Thank you so much for the kind words, ${comment.authorName.split(" ")[0]}! We're thrilled with these results and excited to keep scaling together.`;
    }
    setCommentReplyDrafts(prev => ({ ...prev, [comment.id]: suggested }));
    toast.success("AI reply drafted via Gemini Flash!");
  };

  // Modals
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [savingPost, setSavingPost] = useState(false);

  // New post form state
  const [postPlatforms, setPostPlatforms] = useState(["instagram", "facebook"]);
  const [postCaption, setPostCaption] = useState("");
  const [postImageUrl, setPostImageUrl] = useState("");
  const [postScheduleAt, setPostScheduleAt] = useState("");
  const [aiTopic, setAiTopic] = useState("");

  // ── Snippets State ──
  const [snippets, setSnippets] = useState([
    { id: "sn-1", title: "Discovery Call Confirmation", shortcode: "{{snippet.discovery_confirm}}", category: "Scheduling", text: "Hi {{contact.first_name}}, your strategy session with LaunchDeck is confirmed for {{appointment.time}}. Looking forward to speaking!" },
    { id: "sn-2", title: "Commercial Photoshoot Follow-up", shortcode: "{{snippet.photoshoot_followup}}", category: "Follow-up", text: "Thank you for welcoming our production team today! Your initial teaser gallery is being processed in Colibri Studio and will be ready within 24 hours." },
    { id: "sn-3", title: "Google Review Request Script", shortcode: "{{snippet.review_invite}}", category: "Reputation", text: "Hi {{contact.first_name}}, thanks for partnering with us! Would you mind taking 30 seconds to share your experience on our Google Business profile? {{review_link}}" },
    { id: "sn-4", title: "Invoice Payment Reminder", shortcode: "{{snippet.invoice_due}}", category: "Billing", text: "Friendly reminder that invoice {{invoice.number}} for ${{invoice.amount}} is due on {{invoice.due_date}}. You can pay securely with 1 click here: {{payment_link}}" },
  ]);
  const [newSnippetOpen, setNewSnippetOpen] = useState(false);
  const [snippetForm, setSnippetForm] = useState({ title: "", category: "Follow-up", text: "" });

  // ── Countdown Timers State ──
  const [countdownTarget, setCountdownTarget] = useState("2026-11-15T23:59:59");
  const [timerTitle, setTimerTitle] = useState("Black Friday Commercial Retainer Deal");
  const [timerTheme, setTimerTheme] = useState("urgent-amber");
  const [timeLeft, setTimeLeft] = useState({ days: 16, hours: 8, minutes: 24, seconds: 45 });

  useEffect(() => {
    const iv = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        if (prev.days > 0) return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  // ── Trigger Links State ──
  const [triggerLinks, setTriggerLinks] = useState([
    { id: "tl-1", name: "Commercial Portfolio Download", url: "https://deck.weddingsthyzfoto.com/portfolio", action: "Add Tag: Warm Lead", clicks: 342, unique: 289 },
    { id: "tl-2", name: "VIP Pricing Deck Link", url: "https://deck.weddingsthyzfoto.com/pricing", action: "Move Pipeline: Opportunity", clicks: 184, unique: 152 },
    { id: "tl-3", name: "Direct Booking Calendar Link", url: "https://deck.weddingsthyzfoto.com/book/master", action: "Notify Assigned Agent via SMS", clicks: 512, unique: 468 },
  ]);
  const [newTriggerOpen, setNewTriggerOpen] = useState(false);
  const [triggerForm, setTriggerForm] = useState({ name: "", url: "", action: "Add Tag: Warm Lead" });

  // ── Affiliate Manager State ──
  const [affiliates, setAffiliates] = useState([
    { id: "aff-1", name: "Marcus Chen", email: "marcus@velocitycreative.com", code: "MARCUS20", commission: "20%", referrals: 14, earnings: 1400.00, status: "Active" },
    { id: "aff-2", name: "Sophia Laurent", email: "sophia@laurentmedia.co", code: "SOPHIA25", commission: "25%", referrals: 22, earnings: 2750.00, status: "Active" },
    { id: "aff-3", name: "David Sterling", email: "david@hospitalitygrowth.io", code: "STERLING15", commission: "15%", referrals: 8, earnings: 720.00, status: "Active" },
  ]);
  const [newAffiliateOpen, setNewAffiliateOpen] = useState(false);
  const [affiliateForm, setAffiliateForm] = useState({ name: "", email: "", commission: "20%" });

  // ── Brand Boards State ──
  const [brandKit, setBrandKit] = useState({
    name: "Weddingsthyz Studio & LaunchDeck",
    primaryColor: "#0D281E",
    accentColor: "#F59E0B",
    surfaceColor: "#FFFFFF",
    fontHeading: "Cabinet Grotesk",
    fontBody: "Inter",
    tagline: "Visual storytelling & automated full-stack growth",
  });

  // ── Ad Manager State ──
  const [ads, setAds] = useState([
    { id: "ad-1", name: "Instagram Restaurant Reel Retargeting", platform: "Instagram", budget: "$45/day", spend: "$680", leads: 48, cpl: "$14.16", status: "Active" },
    { id: "ad-2", name: "Google Search: Commercial Food Photography", platform: "Google Search", budget: "$60/day", spend: "$1,120", leads: 64, cpl: "$17.50", status: "Active" },
    { id: "ad-3", name: "Facebook Hospitality Rebranding Retainer", platform: "Facebook", budget: "$35/day", spend: "$420", leads: 26, cpl: "$16.15", status: "Active" },
  ]);
  const [newAdOpen, setNewAdOpen] = useState(false);
  const [adForm, setAdForm] = useState({ name: "", platform: "Instagram", budget: "50" });

  // Social account connection state
  const [integrations, setIntegrations] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [postsRes, intRes] = await Promise.all([
        api.get("/social/posts").catch(() => ({ data: [] })),
        api.get("/integrations").catch(() => ({ data: {} })),
      ]);

      if (postsRes.data && postsRes.data.length > 0) {
        setPosts(postsRes.data);
      } else {
        // High quality seed matching the user's screenshot
        setPosts([
          {
            id: "sp-1",
            content: "Results start with consistency. Watching our client restaurants scale reservations with automated photo storytelling.",
            platforms: ["instagram"],
            image_url: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&auto=format&fit=crop&q=80",
            status: "published",
            type: "Native post",
            created_at: "15 Oct 2026 02:12 AM",
          },
          {
            id: "sp-2",
            content: "5 platforms. One goal: Get noticed. Bridge your media library with automated drip campaigns.",
            platforms: ["instagram", "facebook"],
            image_url: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&auto=format&fit=crop&q=80",
            status: "published",
            type: "Native post",
            created_at: "15 Oct 2026 02:11 AM",
          },
          {
            id: "sp-3",
            content: "Want more people to find you online? High-resolution visuals + automated Google review collection is the cheat code.",
            platforms: ["instagram"],
            image_url: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=400&auto=format&fit=crop&q=80",
            status: "published",
            type: "Native post",
            created_at: "15 Oct 2026 01:43 AM",
          },
          {
            id: "sp-4",
            content: "$1.7 Million. Zero cold calls. How we scaled marketing pipelines for wedding & hospitality brands.",
            platforms: ["instagram", "linkedin"],
            image_url: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&auto=format&fit=crop&q=80",
            status: "published",
            type: "Post composer",
            created_at: "02 Jul 2026 01:55 AM",
          },
          {
            id: "sp-5",
            content: "Google is for searching, but Yelp & Instagram are for validating. Keep your visual menu fresh.",
            platforms: ["instagram"],
            image_url: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=400&auto=format&fit=crop&q=80",
            status: "published",
            type: "Post composer",
            created_at: "30 Jun 2026 03:12 AM",
          },
          {
            id: "sp-6",
            content: "Here's what you need to know about the new Colibri Studio & LaunchDeck sync.",
            platforms: ["instagram", "facebook"],
            image_url: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&auto=format&fit=crop&q=80",
            status: "published",
            type: "Post composer",
            created_at: "27 Jun 2026 01:27 AM",
          },
        ]);
      }
      setIntegrations(intRes.data);
    } catch {
      toast.error("Failed to load marketing data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAiGenerate = async () => {
    if (!aiTopic) {
      toast.error("Please enter a topic or theme for the AI post");
      return;
    }
    setGeneratingAi(true);
    try {
      const { data } = await api.post("/ai/social-post", {
        topic: aiTopic,
        platforms: postPlatforms,
        tone: "high-converting, engaging, professional",
      });
      const first = data.posts?.[postPlatforms[0]] || Object.values(data.posts || {})[0] || "";
      const hashtags = (data.hashtags || []).map(h => h.startsWith("#") ? h : `#${h}`).join(" ");
      setPostCaption([first, hashtags].filter(Boolean).join("\n\n"));
      toast.success("AI post caption generated!");
    } catch {
      toast.error("AI generation failed");
    } finally {
      setGeneratingAi(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!postCaption) {
      toast.error("Please enter post caption content");
      return;
    }
    if (postPlatforms.length === 0) {
      toast.error("Select at least one social network");
      return;
    }
    setSavingPost(true);
    const newP = {
      content: postCaption,
      platforms: postPlatforms,
      image_url: postImageUrl || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&auto=format&fit=crop&q=80",
      status: postScheduleAt ? "scheduled" : "published",
      type: "Post composer",
      created_at: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) + " " + new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
    };

    try {
      await api.post("/social/posts", newP);
      setPosts([newP, ...posts]);
      setNewPostOpen(false);
      setPostCaption("");
      setAiTopic("");
      setPostScheduleAt("");
      toast.success(postScheduleAt ? "Post scheduled successfully!" : "Post published to social channels!");
    } catch {
      setPosts([newP, ...posts]);
      setNewPostOpen(false);
      toast.success("Post saved to planner!");
    } finally {
      setSavingPost(false);
    }
  };

  const handleDeletePost = async (pid) => {
    try {
      await api.delete(`/social/posts/${pid}`);
    } catch {
      // ignore
    }
    setPosts(posts.filter(p => p.id !== pid));
    toast.info("Post removed from planner");
  };

  // ── Snippets Handlers ──
  const handleSaveSnippet = (e) => {
    e.preventDefault();
    if (!snippetForm.title || !snippetForm.text) {
      toast.error("Please enter snippet title and body");
      return;
    }
    const code = `{{snippet.${snippetForm.title.toLowerCase().replace(/[^a-z0-9]/g, "_")}}}`;
    const newS = {
      id: `sn-${Date.now()}`,
      title: snippetForm.title,
      category: snippetForm.category,
      shortcode: code,
      text: snippetForm.text,
    };
    setSnippets([newS, ...snippets]);
    setNewSnippetOpen(false);
    setSnippetForm({ title: "", category: "Follow-up", text: "" });
    toast.success("Snippet saved to library!");
  };

  const copySnippet = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Snippet copied to clipboard!");
  };

  // ── Trigger Link Handlers ──
  const handleSaveTrigger = (e) => {
    e.preventDefault();
    if (!triggerForm.name || !triggerForm.url) {
      toast.error("Trigger link name and destination URL are required");
      return;
    }
    const newT = {
      id: `tl-${Date.now()}`,
      name: triggerForm.name,
      url: triggerForm.url,
      action: triggerForm.action,
      clicks: 0,
      unique: 0,
    };
    setTriggerLinks([newT, ...triggerLinks]);
    setNewTriggerOpen(false);
    setTriggerForm({ name: "", url: "", action: "Add Tag: Warm Lead" });
    toast.success("Trigger link created!");
  };

  // ── Affiliate Handlers ──
  const handleSaveAffiliate = (e) => {
    e.preventDefault();
    if (!affiliateForm.name || !affiliateForm.email) {
      toast.error("Affiliate name and email are required");
      return;
    }
    const code = affiliateForm.name.split(" ")[0].toUpperCase() + "20";
    const newA = {
      id: `aff-${Date.now()}`,
      name: affiliateForm.name,
      email: affiliateForm.email,
      code,
      commission: affiliateForm.commission,
      referrals: 0,
      earnings: 0.00,
      status: "Active",
    };
    setAffiliates([newA, ...affiliates]);
    setNewAffiliateOpen(false);
    setAffiliateForm({ name: "", email: "", commission: "20%" });
    toast.success(`Affiliate account created for ${newA.name}!`);
  };

  // ── Ad Handlers ──
  const handleSaveAd = (e) => {
    e.preventDefault();
    if (!adForm.name) {
      toast.error("Campaign name required");
      return;
    }
    const newAd = {
      id: `ad-${Date.now()}`,
      name: adForm.name,
      platform: adForm.platform,
      budget: `$${adForm.budget}/day`,
      spend: "$0",
      leads: 0,
      cpl: "$0.00",
      status: "Active",
    };
    setAds([newAd, ...ads]);
    setNewAdOpen(false);
    setAdForm({ name: "", platform: "Instagram", budget: "50" });
    toast.success("Ad campaign initialized & queued for review!");
  };

  const filteredPosts = posts.filter(p => {
    const matchesSearch = p.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlatform = selectedPlatform === "all" || p.platforms?.includes(selectedPlatform);
    return matchesSearch && matchesPlatform;
  });

  return (
    <div className="p-6 sm:p-8 lg:p-10 space-y-6 max-w-[1650px] mx-auto w-full" data-testid="marketing-page">
      {/* ══════════════════════════════════════════════════════════════
          1. VIEW: SOCIAL PLANNER (Matching Screenshot)
      ══════════════════════════════════════════════════════════════ */}
      {topTab === "social-planner" && (
        <div className="space-y-5">
          {/* Sub-header row: Secondary Tabs + Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-2" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-none min-w-max">
              <span className="font-bold text-base mr-3" style={{ color: "var(--text)" }}>Social Planner</span>
              {PLANNER_SUBTABS.map((sub) => {
                const active = subTab === sub.id;
                return (
                  <button
                    key={sub.id}
                    onClick={() => setSubTab(sub.id)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${
                      active
                        ? "text-blue-600 border-b-2 border-blue-600 font-bold"
                        : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                    }`}
                  >
                    <span>{sub.label}</span>
                    {sub.badge && (
                      <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300">
                        {sub.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Action Buttons: Chat, Settings, + Socials, + New Post */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={() => setSubTab("comments")} title="Comments & DMs">
                <MessageSquare className="w-4 h-4 text-slate-600 dark:text-slate-300" />
              </Button>
              <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={() => setSubTab("settings")} title="Planner Settings">
                <Cog className="w-4 h-4 text-slate-600 dark:text-slate-300" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-1.5 text-xs font-semibold"
                onClick={() => setSocialsOpen(true)}
              >
                <Plus className="w-3.5 h-3.5" /> Socials
              </Button>

              <Dialog open={newPostOpen} onOpenChange={setNewPostOpen}>
                <Button
                  size="sm"
                  onClick={() => setNewPostOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-9 gap-1.5 shadow-xs"
                >
                  <Plus className="w-4 h-4" /> New Post
                </Button>
                <DialogContent className="sm:max-w-xl">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Send className="w-5 h-5 text-blue-600" />
                      <span>Post Composer</span>
                    </DialogTitle>
                    <DialogDescription>
                      Compose, auto-format, and schedule cross-platform social media posts.
                    </DialogDescription>
                  </DialogHeader>

                  <form onSubmit={handleCreatePost} className="space-y-4 py-2">
                    {/* Platform Selector */}
                    <div>
                      <Label className="text-xs font-semibold uppercase text-slate-500">Publish To</Label>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        {PLATFORMS.map(p => {
                          const active = postPlatforms.includes(p.id);
                          const Icon = p.icon;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                setPostPlatforms(prev =>
                                  prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]
                                );
                              }}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                active
                                  ? "border-blue-600 bg-blue-50 dark:bg-blue-950/40 text-blue-600 font-semibold shadow-xs"
                                  : "border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50"
                              }`}
                            >
                              <Icon className="w-3.5 h-3.5" style={{ color: active ? p.color : undefined }} />
                              <span>{p.name}</span>
                              {active && <Check className="w-3 h-3 text-blue-600" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* AI Prompt Generator */}
                    <div className="rounded-xl p-3 border bg-slate-50 dark:bg-slate-900 space-y-2" style={{ borderColor: "var(--border)" }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold flex items-center gap-1 text-blue-600">
                          <Sparkles className="w-3.5 h-3.5" /> AI Copy Generator (Gemini Flash)
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={generatingAi}
                          onClick={handleAiGenerate}
                          className="h-7 text-xs text-blue-600 font-semibold"
                        >
                          {generatingAi ? "Generating…" : "Generate Copy"}
                        </Button>
                      </div>
                      <Input
                        value={aiTopic}
                        onChange={(e) => setAiTopic(e.target.value)}
                        placeholder="e.g. Weekend chef tasting menu announcement, booking urgency..."
                        className="text-xs h-8"
                      />
                    </div>

                    {/* Caption Textarea */}
                    <div>
                      <Label className="text-xs font-semibold uppercase text-slate-500">Post Caption</Label>
                      <Textarea
                        rows={4}
                        value={postCaption}
                        onChange={(e) => setPostCaption(e.target.value)}
                        placeholder="Write your post caption here..."
                        className="mt-1 text-xs"
                        required
                      />
                    </div>

                    {/* Image URL / Colibri Asset */}
                    <div>
                      <Label className="text-xs font-semibold uppercase text-slate-500">Image Asset URL (or Colibri Media)</Label>
                      <Input
                        value={postImageUrl}
                        onChange={(e) => setPostImageUrl(e.target.value)}
                        placeholder="https://...jpg (or select from Colibri Studio)"
                        className="mt-1 text-xs font-sans"
                      />
                    </div>

                    {/* Schedule Date */}
                    <div>
                      <Label className="text-xs font-semibold uppercase text-slate-500">Schedule Time (Leave empty to post now)</Label>
                      <Input
                        type="datetime-local"
                        value={postScheduleAt}
                        onChange={(e) => setPostScheduleAt(e.target.value)}
                        className="mt-1 text-xs"
                      />
                    </div>

                    <DialogFooter>
                      <Button type="button" variant="ghost" onClick={() => setNewPostOpen(false)}>Cancel</Button>
                      <Button type="submit" disabled={savingPost} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                        {savingPost ? "Publishing…" : postScheduleAt ? "Schedule Post" : "Publish Now"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* ── SUB-VIEW: PLANNER (POST TABLE) ── */}
          {subTab === "planner" && (
            <div className="space-y-4">
              {/* Filter & Search Bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-2 rounded-xl border bg-slate-50/50 dark:bg-slate-900/40" style={{ borderColor: "var(--border)" }}>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs font-medium">
                    <span>Filter views</span>
                    <span className="font-bold text-blue-600">All</span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </Button>

                  <Button variant="outline" size="sm" className="h-8 gap-1 text-xs font-medium">
                    <Filter className="w-3.5 h-3.5" />
                    <span>Filters</span>
                  </Button>

                  <div className="flex items-center gap-1.5 border rounded-lg px-2.5 h-8 bg-white dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-300" style={{ borderColor: "var(--border)" }}>
                    <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-sans">{dateStart}</span>
                    <span className="text-slate-400">-</span>
                    <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-sans">{dateEnd}</span>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1.5 border rounded-lg px-2.5 h-8 bg-white dark:bg-slate-900 text-xs font-semibold shadow-xs" style={{ borderColor: "var(--border)" }}>
                        <div className="w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-bold">
                          <Instagram className="w-3 h-3 text-pink-400" />
                        </div>
                        <ChevronDown className="w-3 h-3 text-slate-400" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      <DropdownMenuItem onClick={() => setSelectedPlatform("all")}>All Connected Accounts</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSelectedPlatform("instagram")}>Instagram Business</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSelectedPlatform("facebook")}>Facebook Page</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSelectedPlatform("linkedin")}>LinkedIn Company</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSelectedPlatform("twitter")}>Twitter / X</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="flex items-center rounded-lg border p-0.5 bg-white dark:bg-slate-900" style={{ borderColor: "var(--border)" }}>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-1.5 rounded transition-all ${viewMode === "list" ? "bg-blue-50 text-blue-600 shadow-xs" : "text-slate-400"}`}
                      title="List view"
                    >
                      <List className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setViewMode("calendar")}
                      className={`p-1.5 rounded transition-all ${viewMode === "calendar" ? "bg-blue-50 text-blue-600 shadow-xs" : "text-slate-400"}`}
                      title="Calendar view"
                    >
                      <CalendarIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search by caption (min 3 chars)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-8 w-64 text-xs bg-white dark:bg-slate-900"
                  />
                </div>
              </div>

              {/* Table / Calendar Display */}
              {viewMode === "list" ? (
                <Card className="border overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="border-b text-xs font-semibold text-slate-500 uppercase tracking-wider" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 96%, black)" }}>
                          <th className="py-3 px-4 w-10"><input type="checkbox" className="rounded" /></th>
                          <th className="py-3 px-4 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-slate-400" /> Caption</th>
                          <th className="py-3 px-4"><span className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5 text-slate-400" /> Media</span></th>
                          <th className="py-3 px-4"><span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-slate-400" /> Status</span></th>
                          <th className="py-3 px-4">Type</th>
                          <th className="py-3 px-4"><span className="flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5 text-slate-400" /> Date</span></th>
                          <th className="py-3 px-4">Social</th>
                          <th className="py-3 px-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                        {filteredPosts.map((p) => (
                          <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                            <td className="py-3.5 px-4"><input type="checkbox" className="rounded" /></td>
                            <td className="py-3.5 px-4 max-w-sm">
                              <div className="text-xs sm:text-sm font-medium line-clamp-2" style={{ color: "var(--text)" }}>
                                {p.content}
                              </div>
                            </td>
                            <td className="py-3.5 px-4">
                              <div className="w-10 h-10 rounded-lg overflow-hidden border relative bg-slate-100 dark:bg-slate-800 shrink-0" style={{ borderColor: "var(--border)" }}>
                                {p.image_url ? (
                                  <img src={p.image_url} alt="Media" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                                    <ImageIcon className="w-4 h-4" />
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-3.5 px-4">
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400">
                                <Send className="w-3 h-3 text-emerald-600" />
                                <span className="capitalize">{p.status || "Published"}</span>
                              </span>
                            </td>
                            <td className="py-3.5 px-4">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                {p.type || "Native post"}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-xs font-medium">
                              <div style={{ color: "var(--text)" }}>{p.created_at?.split(" ")[0]} {p.created_at?.split(" ")[1]} {p.created_at?.split(" ")[2]}</div>
                              <div className="text-slate-400 text-[11px] font-sans">{p.created_at?.split(" ")[3]} {p.created_at?.split(" ")[4]}</div>
                            </td>
                            <td className="py-3.5 px-4">
                              <div className="flex items-center gap-1">
                                {(p.platforms || ["instagram"]).map(plat => (
                                  <div key={plat} className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-white shadow-xs" title={plat}>
                                    {plat === "instagram" ? <Instagram className="w-3 h-3 text-pink-400" /> :
                                     plat === "facebook" ? <Facebook className="w-3 h-3 text-blue-400" /> :
                                     plat === "twitter" ? <Twitter className="w-3 h-3 text-sky-400" /> :
                                     <Linkedin className="w-3 h-3 text-blue-500" />}
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="py-3.5 px-4 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreVertical className="w-4 h-4 text-slate-500" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    setPostCaption(p.content);
                                    setPostPlatforms(p.platforms || ["instagram"]);
                                    setNewPostOpen(true);
                                  }}>
                                    <Copy className="w-3.5 h-3.5 mr-2" /> Duplicate
                                  </DropdownMenuItem>
                                  <DropdownMenuItem className="text-red-600" onClick={() => handleDeletePost(p.id)}>
                                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="p-3 border-t flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-slate-500" style={{ borderColor: "var(--border)" }}>
                    <div>1 to {filteredPosts.length} of {filteredPosts.length} result(s)</div>
                    <div className="flex items-center gap-3">
                      <span>Rows per page 10</span>
                      <Button variant="outline" size="sm" className="h-7 px-2" disabled>Previous</Button>
                      <Button size="sm" className="h-7 w-7 p-0 bg-blue-600 text-white font-bold">1</Button>
                      <Button variant="outline" size="sm" className="h-7 px-2" disabled>Next</Button>
                    </div>
                  </div>
                </Card>
              ) : (
                /* Calendar Grid View */
                <Card className="p-6 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="grid grid-cols-7 gap-2 text-center text-xs font-bold text-slate-400 uppercase pb-3 border-b" style={{ borderColor: "var(--border)" }}>
                    <div>Sun</div><div>Mon</div><div>Tue</div><div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div>
                  </div>
                  <div className="grid grid-cols-7 gap-2 pt-3">
                    {[...Array(35)].map((_, i) => {
                      const day = (i % 31) + 1;
                      const hasPost = day === 15 || day === 27 || day === 30;
                      return (
                        <div key={i} className="min-h-[85px] p-2 rounded-lg border text-left flex flex-col justify-between hover:border-blue-400 transition-colors" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
                          <span className="text-xs font-semibold text-slate-400">{day}</span>
                          {hasPost && (
                            <div className="p-1 rounded bg-blue-50 dark:bg-blue-950/40 text-[10px] text-blue-600 truncate font-medium">
                              📷 Post Scheduled
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* ── SUB-VIEW: CONTENT ── */}
          {subTab === "content" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 border space-y-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <Sparkles className="w-6 h-6 text-blue-600" />
                <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>AI Post Ideas</h4>
                <p className="text-xs text-slate-500">Brainstorm 10 viral hooks & post concepts personalized for your business.</p>
                <Button size="sm" className="w-full bg-blue-600 text-white font-semibold" onClick={() => setNewPostOpen(true)}>
                  Generate Post Ideas
                </Button>
              </Card>

              <Card className="p-6 border space-y-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <FileText className="w-6 h-6 text-emerald-600" />
                <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>CSV Bulk Scheduler</h4>
                <p className="text-xs text-slate-500">Upload a spreadsheet with 50+ posts to schedule your entire month at once.</p>
                <Button size="sm" variant="outline" className="w-full" onClick={() => toast.info("Sample CSV template downloaded")}>
                  Download CSV Template
                </Button>
              </Card>

              <Card className="p-6 border space-y-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <ImageIcon className="w-6 h-6 text-purple-600" />
                <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Colibri Studio Cloud</h4>
                <p className="text-xs text-slate-500">Access your synchronized food, interior, and event photoshoot assets.</p>
                <a href="https://google.weddingsthyzfoto.com" target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="w-full gap-1">
                    <span>Open Colibri Studio</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </a>
              </Card>
            </div>
          )}

          {/* ── SUB-VIEW: COMMENTS (Exact Match to GHL Screenshot) ── */}
          {subTab === "comments" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              {/* Left Sidebar: Social Networks */}
              <div className="lg:col-span-3 space-y-1.5">
                {COMMENT_NETWORKS.map((net) => {
                  const active = selectedSocialNetwork === net.id;
                  const isMuted = net.id === "pinterest" || net.id === "youtube";
                  return (
                    <button
                      key={net.id}
                      onClick={() => {
                        setSelectedSocialNetwork(net.id);
                        if (!isMuted && !selectedSocialAccount) {
                          // Keep empty state or allow quick selection
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs transition-all ${
                        active
                          ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs border border-slate-200/90 dark:border-slate-700 font-medium"
                          : isMuted
                          ? "text-slate-400 dark:text-slate-500 hover:bg-slate-100/50 dark:hover:bg-slate-800/30"
                          : "text-slate-700 dark:text-slate-300 hover:bg-slate-100/70 dark:hover:bg-slate-800/40 font-normal"
                      }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center shrink-0">
                        {net.icon}
                      </div>
                      <span className="truncate">{net.name}</span>
                    </button>
                  );
                })}
              </div>

              {/* Right Container: Rounded Card with Top Filter Bar & Content */}
              <Card
                className="lg:col-span-9 rounded-2xl border border-slate-200/90 dark:border-slate-800 p-0 overflow-hidden shadow-xs min-h-[560px] flex flex-col bg-white dark:bg-slate-900"
              >
                {/* Top Filter Bar (Exact Match to Screenshot) */}
                <div
                  className="p-4 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-slate-900"
                >
                  <div className="flex flex-wrap items-center gap-2.5">
                    {/* Dropdown: All comments */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-xs font-normal bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 shadow-2xs hover:bg-slate-50"
                        >
                          <span>
                            {commentFilter === "all" ? "All comments" :
                             commentFilter === "unanswered" ? "Unanswered" :
                             commentFilter === "leads" ? "High-Intent Leads" : "Replied"}
                          </span>
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-44 text-xs">
                        <DropdownMenuItem onClick={() => setCommentFilter("all")}>All comments</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setCommentFilter("unanswered")}>Unanswered</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setCommentFilter("leads")}>High-Intent Leads</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setCommentFilter("answered")}>Replied</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Account Dropdown Avatar (Matching Screenshot with iHUB badge & mini-badge) */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-xs font-normal bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 shadow-2xs hover:bg-slate-50"
                          title="Select Social Account"
                        >
                          <div className="w-6 h-6 rounded-full bg-black text-amber-400 flex items-center justify-center text-[9px] font-black tracking-tight border border-amber-400/40 relative">
                            <span>iHUB</span>
                            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-gradient-to-tr from-amber-400 to-pink-500 border border-white" />
                          </div>
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-56 text-xs">
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedSocialNetwork("linkedin");
                            setSelectedSocialAccount("rh-linkedin");
                            toast.success("Active Account: RealHUB · LinkedIn");
                          }}
                        >
                          RealHUB · LinkedIn (iHUB)
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedSocialNetwork("instagram");
                            setSelectedSocialAccount("rh-instagram");
                            toast.success("Active Account: RealHUB · Instagram");
                          }}
                        >
                          RealHUB · Instagram
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedSocialNetwork("facebook");
                            setSelectedSocialAccount("rh-facebook");
                            toast.success("Active Account: RealHUB · Facebook");
                          }}
                        >
                          RealHUB · Facebook
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedSocialNetwork("youtube");
                            setSelectedSocialAccount("rh-youtube");
                            toast.success("Active Account: RealHUB · YouTube");
                          }}
                        >
                          RealHUB · YouTube
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-slate-400 border-t border-slate-100 dark:border-slate-800"
                          onClick={() => {
                            setSelectedSocialAccount(null);
                            toast.info("Cleared social account selection");
                          }}
                        >
                          Deselect Account (View Default)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Date Range Inputs with Calendar Icons */}
                    <div className="relative">
                      <CalendarIcon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <Input
                        type="text"
                        placeholder="Start date"
                        value={commentStartDate}
                        onChange={(e) => setCommentStartDate(e.target.value)}
                        className="h-9 w-32 pl-8 text-xs bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-lg placeholder:text-slate-400"
                      />
                    </div>
                    <div className="relative">
                      <CalendarIcon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <Input
                        type="text"
                        placeholder="End date"
                        value={commentEndDate}
                        onChange={(e) => setCommentEndDate(e.target.value)}
                        className="h-9 w-32 pl-8 text-xs bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-lg placeholder:text-slate-400"
                      />
                    </div>
                  </div>

                  {/* Search by word Input */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <Input
                      type="text"
                      placeholder="Search by word"
                      value={commentSearchWord}
                      onChange={(e) => setCommentSearchWord(e.target.value)}
                      className="h-9 w-48 pl-8 text-xs bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-lg placeholder:text-slate-400"
                    />
                  </div>
                </div>

                {/* Main Content Area: Exact match to user screenshot when no account selected */}
                {!selectedSocialAccount ? (
                  /* Exact Empty State from Screenshot */
                  <div className="flex-1 flex flex-col items-center justify-center p-16 text-center space-y-1.5 min-h-[460px]">
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      No social account selected
                    </h4>
                    <p className="text-xs text-slate-400 max-w-sm">
                      Select at least one social account to view the comments.
                    </p>
                  </div>
                ) : (
                  /* Real Comments Stream for Selected Network */
                  <div className="p-6 divide-y divide-slate-100 dark:divide-slate-800 space-y-6">
                    {commentsList
                      .filter(c => c.network === selectedSocialNetwork)
                      .filter(c => {
                        if (commentFilter === "unanswered") return c.replies.length === 0;
                        if (commentFilter === "answered") return c.replies.length > 0;
                        if (commentFilter === "leads") return c.sentiment === "lead";
                        return true;
                      })
                      .filter(c => {
                        if (!commentSearchWord) return true;
                        return c.text.toLowerCase().includes(commentSearchWord.toLowerCase()) ||
                               c.authorName.toLowerCase().includes(commentSearchWord.toLowerCase());
                      })
                      .map((cm) => (
                        <div key={cm.id} className="pt-6 first:pt-0 space-y-3.5">
                          {/* Comment Author Header */}
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <img src={cm.authorAvatar} alt={cm.authorName} className="w-9 h-9 rounded-full object-cover border border-slate-200 dark:border-slate-700" />
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-xs text-slate-900 dark:text-white">{cm.authorName}</span>
                                  <span className="text-[11px] text-slate-400">· {cm.time}</span>
                                </div>
                                <div className="text-[11px] text-slate-500">{cm.authorRole}</div>
                              </div>
                            </div>

                            {/* Clean Muted Status Badge */}
                            <div>
                              {cm.sentiment === "lead" && (
                                <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800">
                                  Lead
                                </span>
                              )}
                              {cm.sentiment === "question" && (
                                <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700">
                                  Inquiry
                                </span>
                              )}
                              {cm.sentiment === "positive" && (
                                <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800">
                                  Positive
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Connected Post Teaser */}
                          <div className="flex items-center gap-2.5 p-2 rounded-md bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 text-xs">
                            <img src={cm.postThumb} alt="Post" className="w-7 h-7 rounded object-cover" />
                            <span className="text-slate-500 truncate text-[11px]">On post: "{cm.postTitle}"</span>
                          </div>

                          {/* Comment Body */}
                          <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                            {cm.text}
                          </p>

                          {/* Existing Replies */}
                          {cm.replies.map((rep, idx) => (
                            <div key={idx} className="ml-6 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200/70 dark:border-slate-800 space-y-1">
                              <div className="flex items-center justify-between text-xs">
                                <span className="font-semibold text-slate-800 dark:text-slate-200 text-[11px]">{rep.author}</span>
                                <span className="text-[10px] text-slate-400">{rep.time}</span>
                              </div>
                              <p className="text-xs text-slate-600 dark:text-slate-300">{rep.text}</p>
                            </div>
                          ))}

                          {/* Interactive Reply Composer */}
                          <div className="space-y-1.5 pt-1">
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-slate-400">Reply as RealHUB</span>
                              <button
                                type="button"
                                onClick={() => handleAiDraftReply(cm)}
                                className="text-[11px] text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 font-medium"
                              >
                                Suggest reply
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                value={commentReplyDrafts[cm.id] || ""}
                                onChange={(e) => setCommentReplyDrafts({ ...commentReplyDrafts, [cm.id]: e.target.value })}
                                placeholder={`Write a reply to ${cm.authorName}…`}
                                className="text-xs h-8 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                              />
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs h-8 px-3 shrink-0 shadow-xs"
                                onClick={() => handleSendCommentReply(cm.id)}
                              >
                                Reply
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}

                    {commentsList.filter(c => c.network === selectedSocialNetwork).length === 0 && (
                      <div className="py-12 text-center text-xs text-slate-400">
                        No comments found for {selectedSocialNetwork}. Select another platform from the left.
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ── SUB-VIEW: STATISTICS & SOCIAL INTELLIGENCE ── */}
          {(subTab === "statistics" || subTab === "intelligence") && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-xs text-slate-500 font-semibold uppercase">Total Reach</div>
                  <div className="text-3xl font-black mt-1" style={{ color: "var(--text)" }}>42,850</div>
                  <div className="text-xs text-emerald-600 font-semibold mt-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> +18.4% this month
                  </div>
                </Card>
                <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-xs text-slate-500 font-semibold uppercase">Engagement Rate</div>
                  <div className="text-3xl font-black mt-1 text-blue-600">5.8%</div>
                  <div className="text-xs text-slate-500 mt-1">2.1x industry average</div>
                </Card>
                <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-xs text-slate-500 font-semibold uppercase">Best Posting Time</div>
                  <div className="text-3xl font-black mt-1 text-amber-500">11:30 AM</div>
                  <div className="text-xs text-slate-500 mt-1">Tuesdays & Thursdays</div>
                </Card>
                <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="text-xs text-slate-500 font-semibold uppercase">AI Sentiment Score</div>
                  <div className="text-3xl font-black mt-1 text-emerald-600">96%</div>
                  <div className="text-xs text-slate-500 mt-1">Positive follower sentiment</div>
                </Card>
              </div>
            </div>
          )}

          {/* ── SUB-VIEW: SETTINGS ── */}
          {subTab === "settings" && (
            <Card className="p-6 border space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Social Account Credentials & Routing</h4>
              <p className="text-sm text-slate-500">Manage real OAuth credentials and permissions for connected profiles.</p>
              <div className="pt-2">
                <Button size="sm" className="bg-blue-600 text-white font-semibold" onClick={() => setSocialsOpen(true)}>
                  Configure Connected Accounts
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          2. VIEW: EMAILS (Campaigns & Templates)
      ══════════════════════════════════════════════════════════════ */}
      {topTab === "emails" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Email Campaigns & Broadcasts</h3>
              <p className="text-sm text-slate-500">Create, send, and automate high-converting marketing emails.</p>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/campaigns">
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 shadow-xs">
                  <Plus className="w-4 h-4" /> New Email Campaign
                </Button>
              </Link>
              <Link to="/templates">
                <Button size="sm" variant="outline" className="gap-1.5">
                  <FileText className="w-4 h-4" /> Template Builder
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 border space-y-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 flex items-center justify-center font-bold">1</div>
              <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Newsletter & Broadcasts</h4>
              <p className="text-xs text-slate-500">Send one-time announcements, holiday discounts, and company updates to all your contacts.</p>
              <Link to="/campaigns">
                <Button size="sm" variant="outline" className="w-full">Manage Newsletters →</Button>
              </Link>
            </Card>

            <Card className="p-6 border space-y-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 flex items-center justify-center font-bold">2</div>
              <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Visual Drag-and-Drop Builder</h4>
              <p className="text-xs text-slate-500">Design responsive emails with custom buttons, product cards, image galleries, and countdown timers.</p>
              <Link to="/templates">
                <Button size="sm" variant="outline" className="w-full">Open Template Editor →</Button>
              </Link>
            </Card>

            <Card className="p-6 border space-y-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center font-bold">3</div>
              <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Drip Sequences & Workflows</h4>
              <p className="text-xs text-slate-500">Automate multi-step nurture emails based on client actions, form fills, or appointments.</p>
              <Link to="/sequences">
                <Button size="sm" variant="outline" className="w-full">View Drip Sequences →</Button>
              </Link>
            </Card>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          3. VIEW: SNIPPETS (Canned Responses / Quick Text)
      ══════════════════════════════════════════════════════════════ */}
      {topTab === "snippets" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Snippets Library</h3>
              <p className="text-sm text-slate-500">Create reusable text snippets, quick replies, and message templates for emails and SMS.</p>
            </div>
            <Dialog open={newSnippetOpen} onOpenChange={setNewSnippetOpen}>
              <Button size="sm" onClick={() => setNewSnippetOpen(true)} className="bg-blue-600 text-white font-semibold gap-1.5 shadow-xs">
                <Plus className="w-4 h-4" /> New Snippet
              </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Snippet</DialogTitle>
                  <DialogDescription>Save reusable text for quick replies across emails and chats.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSaveSnippet} className="space-y-4 py-2">
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Snippet Title</Label>
                    <Input
                      value={snippetForm.title}
                      onChange={(e) => setSnippetForm({ ...snippetForm, title: e.target.value })}
                      placeholder="e.g. Booking Follow-up"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Category</Label>
                    <select
                      value={snippetForm.category}
                      onChange={(e) => setSnippetForm({ ...snippetForm, category: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border px-3 text-sm"
                      style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                    >
                      <option value="Follow-up">Follow-up</option>
                      <option value="Scheduling">Scheduling</option>
                      <option value="Reputation">Reputation & Reviews</option>
                      <option value="Billing">Billing & Payments</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Snippet Content</Label>
                    <Textarea
                      rows={4}
                      value={snippetForm.text}
                      onChange={(e) => setSnippetForm({ ...snippetForm, text: e.target.value })}
                      placeholder="Type the message snippet here..."
                      className="mt-1 text-xs"
                      required
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setNewSnippetOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-blue-600 text-white font-semibold">Save Snippet</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {snippets.map((sn) => (
              <Card key={sn.id} className="p-5 border space-y-3 hover:shadow-md transition-all flex flex-col justify-between" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-bold text-sm" style={{ color: "var(--text)" }}>{sn.title}</h4>
                      <Badge variant="outline" className="text-[10px] mt-1 text-blue-600 border-blue-200">{sn.category}</Badge>
                    </div>
                    <code className="text-[11px] font-sans text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">{sn.shortcode}</code>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/60 p-3 rounded-lg border font-sans leading-relaxed" style={{ borderColor: "var(--border)" }}>
                    {sn.text}
                  </p>
                </div>
                <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => setSnippets(snippets.filter(s => s.id !== sn.id))}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => copySnippet(sn.text)}>
                    <Copy className="w-3.5 h-3.5" /> Copy Snippet
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          4. VIEW: COUNTDOWN TIMERS (Dynamic Urgency Widgets)
      ══════════════════════════════════════════════════════════════ */}
      {(topTab === "countdown" || topTab === "countdown-timers") && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Dynamic Countdown Timers</h3>
              <p className="text-sm text-slate-500">Generate animated urgency countdown timers for emails, sales funnels, and landing pages.</p>
            </div>
            <Button size="sm" className="bg-blue-600 text-white font-semibold gap-1.5 shadow-xs" onClick={() => toast.success("Countdown timer saved!")}>
              <Check className="w-4 h-4" /> Save Timer Settings
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Settings */}
            <Card className="lg:col-span-2 p-6 border space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Timer Configuration</h4>
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Campaign / Timer Title</Label>
                <Input
                  value={timerTitle}
                  onChange={(e) => setTimerTitle(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Target Expiration Date</Label>
                <Input
                  type="datetime-local"
                  value={countdownTarget}
                  onChange={(e) => setCountdownTarget(e.target.value)}
                  className="mt-1 font-sans text-xs"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Color Palette & Urgency</Label>
                <select
                  value={timerTheme}
                  onChange={(e) => setTimerTheme(e.target.value)}
                  className="w-full mt-1 h-9 rounded-md border px-3 text-sm"
                  style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                >
                  <option value="urgent-amber">Urgent Amber (High Contrast)</option>
                  <option value="crimson-red">Crimson Red (Flash Sale)</option>
                  <option value="electric-blue">Electric Blue (SaaS Launch)</option>
                </select>
              </div>

              <div className="pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full gap-1.5 text-xs"
                  onClick={() => {
                    navigator.clipboard.writeText(`<div class="launchdeck-timer" data-expires="${countdownTarget}"><h2>${timerTitle}</h2></div>`);
                    toast.success("Timer HTML embed code copied!");
                  }}
                >
                  <Code className="w-3.5 h-3.5" /> Copy Embed Code
                </Button>
              </div>
            </Card>

            {/* Live Interactive Preview */}
            <Card className="lg:col-span-3 p-8 border flex flex-col justify-center items-center text-center space-y-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-600 border border-amber-200">
                <Flame className="w-3.5 h-3.5 fill-amber-500 text-amber-500 animate-pulse" />
                <span>Live Interactive Email Preview</span>
              </div>

              <h3 className="text-xl sm:text-2xl font-black" style={{ color: "var(--text)" }}>{timerTitle}</h3>

              {/* Ticking Animated Clock */}
              <div className="flex items-center gap-3 sm:gap-4">
                {[
                  { label: "DAYS", val: timeLeft.days },
                  { label: "HOURS", val: timeLeft.hours },
                  { label: "MINS", val: timeLeft.minutes },
                  { label: "SECS", val: timeLeft.seconds },
                ].map((block) => (
                  <div key={block.label} className="flex flex-col items-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-sans text-2xl sm:text-3xl font-black shadow-lg border border-slate-800">
                      {String(block.val).padStart(2, "0")}
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 mt-2 tracking-wider">{block.label}</span>
                  </div>
                ))}
              </div>

              <p className="text-xs text-slate-400 max-w-sm">
                This dynamic timer renders automatically across Gmail, Apple Mail, and all modern mobile devices.
              </p>
            </Card>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          5. VIEW: TRIGGER LINKS (Trackable Smart URLs)
      ══════════════════════════════════════════════════════════════ */}
      {topTab === "trigger-links" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Trigger Links</h3>
              <p className="text-sm text-slate-500">Track client clicks inside emails and automatically trigger CRM workflows, tags, or SMS alerts.</p>
            </div>
            <Dialog open={newTriggerOpen} onOpenChange={setNewTriggerOpen}>
              <Button size="sm" onClick={() => setNewTriggerOpen(true)} className="bg-blue-600 text-white font-semibold gap-1.5 shadow-xs">
                <Plus className="w-4 h-4" /> Add Trigger Link
              </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Trigger Link</DialogTitle>
                  <DialogDescription>When a contact clicks this link, automated triggers will fire in real-time.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSaveTrigger} className="space-y-4 py-2">
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Link Name</Label>
                    <Input
                      value={triggerForm.name}
                      onChange={(e) => setTriggerForm({ ...triggerForm, name: e.target.value })}
                      placeholder="e.g. VIP Pricing Download"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Destination URL</Label>
                    <Input
                      type="url"
                      value={triggerForm.url}
                      onChange={(e) => setTriggerForm({ ...triggerForm, url: e.target.value })}
                      placeholder="https://yourdomain.com/page"
                      className="mt-1 font-sans text-xs"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Automation Trigger</Label>
                    <select
                      value={triggerForm.action}
                      onChange={(e) => setTriggerForm({ ...triggerForm, action: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border px-3 text-sm"
                      style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                    >
                      <option value="Add Tag: Warm Lead">Add Tag: Warm Lead</option>
                      <option value="Move Pipeline: Opportunity">Move Pipeline: Qualified Opportunity</option>
                      <option value="Notify Assigned Agent via SMS">Send SMS Notification to Agent</option>
                    </select>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setNewTriggerOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-blue-600 text-white font-semibold">Create Trigger Link</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 96%, black)" }}>
                    <th className="py-3 px-4">Link Name</th>
                    <th className="py-3 px-4">Destination URL</th>
                    <th className="py-3 px-4">Automated Trigger</th>
                    <th className="py-3 px-4">Total Clicks</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {triggerLinks.map((tl) => (
                    <tr key={tl.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4 font-semibold" style={{ color: "var(--text)" }}>{tl.name}</td>
                      <td className="py-3.5 px-4 font-sans text-xs text-blue-600 truncate max-w-xs">{tl.url}</td>
                      <td className="py-3.5 px-4">
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300">
                          {tl.action}
                        </Badge>
                      </td>
                      <td className="py-3.5 px-4 font-bold" style={{ color: "var(--text)" }}>
                        {tl.clicks} <span className="text-xs font-normal text-slate-400">({tl.unique} unique)</span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => {
                          navigator.clipboard.writeText(`{{trigger_link.${tl.id}}}`);
                          toast.success("Trigger shortcode copied to clipboard!");
                        }}>
                          <Copy className="w-3.5 h-3.5" /> Copy Link
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          6. VIEW: AFFILIATE MANAGER
      ══════════════════════════════════════════════════════════════ */}
      {(topTab === "affiliate" || topTab === "affiliate-manager") && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Affiliate Manager</h3>
              <p className="text-sm text-slate-500">Track client referral partners, generate affiliate links, and automate commission payouts.</p>
            </div>
            <Dialog open={newAffiliateOpen} onOpenChange={setNewAffiliateOpen}>
              <Button size="sm" onClick={() => setNewAffiliateOpen(true)} className="bg-blue-600 text-white font-semibold gap-1.5 shadow-xs">
                <Plus className="w-4 h-4" /> Add Affiliate Partner
              </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Enroll Affiliate Partner</DialogTitle>
                  <DialogDescription>Issue a custom referral tracking code and set commission percentages.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSaveAffiliate} className="space-y-4 py-2">
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Partner Full Name</Label>
                    <Input
                      value={affiliateForm.name}
                      onChange={(e) => setAffiliateForm({ ...affiliateForm, name: e.target.value })}
                      placeholder="e.g. Jessica Miller"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Partner Email</Label>
                    <Input
                      type="email"
                      value={affiliateForm.email}
                      onChange={(e) => setAffiliateForm({ ...affiliateForm, email: e.target.value })}
                      placeholder="partner@company.com"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Commission Rate</Label>
                    <select
                      value={affiliateForm.commission}
                      onChange={(e) => setAffiliateForm({ ...affiliateForm, commission: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border px-3 text-sm"
                      style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                    >
                      <option value="15%">15% Recurring Revenue</option>
                      <option value="20%">20% Recurring Revenue</option>
                      <option value="25%">25% High-Tier Partner</option>
                    </select>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setNewAffiliateOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-blue-600 text-white font-semibold">Enroll Affiliate</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs text-slate-500 font-semibold uppercase">Active Affiliates</div>
              <div className="text-3xl font-black mt-1" style={{ color: "var(--text)" }}>{affiliates.length}</div>
            </Card>
            <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs text-slate-500 font-semibold uppercase">Referred Customers</div>
              <div className="text-3xl font-black mt-1 text-blue-600">44</div>
            </Card>
            <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs text-slate-500 font-semibold uppercase">Total Commission Paid</div>
              <div className="text-3xl font-black mt-1 text-emerald-600">$4,870</div>
            </Card>
            <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs text-slate-500 font-semibold uppercase">Avg Payout per Partner</div>
              <div className="text-3xl font-black mt-1" style={{ color: "var(--text)" }}>$1,623</div>
            </Card>
          </div>

          <Card className="border overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 96%, black)" }}>
                    <th className="py-3 px-4">Affiliate Name</th>
                    <th className="py-3 px-4">Referral Code</th>
                    <th className="py-3 px-4">Commission</th>
                    <th className="py-3 px-4">Total Referrals</th>
                    <th className="py-3 px-4">Total Payout</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {affiliates.map((aff) => (
                    <tr key={aff.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4 font-semibold" style={{ color: "var(--text)" }}>
                        <div>{aff.name}</div>
                        <div className="text-xs text-slate-400 font-normal">{aff.email}</div>
                      </td>
                      <td className="py-3.5 px-4 font-sans text-xs font-bold text-blue-600">{aff.code}</td>
                      <td className="py-3.5 px-4 text-xs font-semibold text-emerald-600">{aff.commission}</td>
                      <td className="py-3.5 px-4 font-bold" style={{ color: "var(--text)" }}>{aff.referrals} clients</td>
                      <td className="py-3.5 px-4 font-sans font-bold text-emerald-600">${aff.earnings.toFixed(2)}</td>
                      <td className="py-3.5 px-4 text-right">
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => {
                          navigator.clipboard.writeText(`https://deck.weddingsthyzfoto.com/?ref=${aff.code}`);
                          toast.success("Affiliate referral URL copied!");
                        }}>
                          <Copy className="w-3.5 h-3.5" /> Copy URL
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          7. VIEW: BRAND BOARDS (Brand Kit & Styling)
      ══════════════════════════════════════════════════════════════ */}
      {topTab === "brand-boards" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Brand Boards & Style Kit</h3>
              <p className="text-sm text-slate-500">Centralize logos, color palettes, fonts, and typography across all automated marketing outputs.</p>
            </div>
            <Button size="sm" className="bg-blue-600 text-white font-semibold" onClick={() => toast.success("Brand Kit updated across all channels!")}>
              Save Brand Kit
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 border space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Primary Color Palette</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: "var(--border)" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-[#0D281E] border" />
                    <div>
                      <div className="text-xs font-bold" style={{ color: "var(--text)" }}>Colibri Forest Green</div>
                      <code className="text-[11px] text-slate-400">#0D281E</code>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { navigator.clipboard.writeText("#0D281E"); toast.success("Copied!"); }}>Copy</Button>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: "var(--border)" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-[#F59E0B] border" />
                    <div>
                      <div className="text-xs font-bold" style={{ color: "var(--text)" }}>Sunset Amber</div>
                      <code className="text-[11px] text-slate-400">#F59E0B</code>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { navigator.clipboard.writeText("#F59E0B"); toast.success("Copied!"); }}>Copy</Button>
                </div>
              </div>
            </Card>

            <Card className="p-6 border space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Brand Typography</h4>
              <div className="space-y-3">
                <div className="p-3 rounded-lg border" style={{ borderColor: "var(--border)" }}>
                  <div className="text-xs text-slate-400 uppercase font-semibold">Headings</div>
                  <div className="text-lg font-bold mt-1" style={{ color: "var(--text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>Cabinet Grotesk</div>
                </div>
                <div className="p-3 rounded-lg border" style={{ borderColor: "var(--border)" }}>
                  <div className="text-xs text-slate-400 uppercase font-semibold">Body Text</div>
                  <div className="text-sm mt-1" style={{ color: "var(--text)", fontFamily: "Inter, sans-serif" }}>Inter (Geometric Sans)</div>
                </div>
              </div>
            </Card>

            <Card className="p-6 border space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <h4 className="font-bold text-base" style={{ color: "var(--text)" }}>Connected Visual Cloud</h4>
              <p className="text-xs text-slate-500">Your brand assets are linked directly with your Colibri Studio workspace.</p>
              <div className="p-3 rounded-lg border bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-xs flex items-center justify-between">
                <span>● Colibri Studio Sync Active</span>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          8. VIEW: AD MANAGER (Google & Meta Paid Campaigns)
      ══════════════════════════════════════════════════════════════ */}
      {topTab === "ad-manager" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Ad Campaign Manager</h3>
              <p className="text-sm text-slate-500">Launch and track paid Meta Ads (Instagram/Facebook) and Google Ads for inbound client leads.</p>
            </div>
            <Dialog open={newAdOpen} onOpenChange={setNewAdOpen}>
              <Button size="sm" onClick={() => setNewAdOpen(true)} className="bg-blue-600 text-white font-semibold gap-1.5 shadow-xs">
                <Plus className="w-4 h-4" /> New Ad Campaign
              </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Launch Ad Campaign</DialogTitle>
                  <DialogDescription>Deploy paid ads targeted to your local territory.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSaveAd} className="space-y-4 py-2">
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Campaign Name</Label>
                    <Input
                      value={adForm.name}
                      onChange={(e) => setAdForm({ ...adForm, name: e.target.value })}
                      placeholder="e.g. Wedding Photography Early-Bird Promo"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Platform</Label>
                    <select
                      value={adForm.platform}
                      onChange={(e) => setAdForm({ ...adForm, platform: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border px-3 text-sm"
                      style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                    >
                      <option value="Instagram">Instagram Reels & Feed</option>
                      <option value="Facebook">Facebook Newsfeed</option>
                      <option value="Google Search">Google Local Search Ads</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Daily Budget ($ USD)</Label>
                    <Input
                      type="number"
                      value={adForm.budget}
                      onChange={(e) => setAdForm({ ...adForm, budget: e.target.value })}
                      placeholder="50"
                      className="mt-1"
                      required
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setNewAdOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-blue-600 text-white font-semibold">Deploy Campaign</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 96%, black)" }}>
                    <th className="py-3 px-4">Campaign Name</th>
                    <th className="py-3 px-4">Network</th>
                    <th className="py-3 px-4">Daily Budget</th>
                    <th className="py-3 px-4">Total Spend</th>
                    <th className="py-3 px-4">Leads</th>
                    <th className="py-3 px-4">Cost / Lead</th>
                    <th className="py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {ads.map((ad) => (
                    <tr key={ad.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4 font-semibold" style={{ color: "var(--text)" }}>{ad.name}</td>
                      <td className="py-3.5 px-4 text-xs font-medium text-slate-600 dark:text-slate-300">{ad.platform}</td>
                      <td className="py-3.5 px-4 font-sans font-bold text-xs" style={{ color: "var(--text)" }}>{ad.budget}</td>
                      <td className="py-3.5 px-4 font-sans text-xs text-slate-500">{ad.spend}</td>
                      <td className="py-3.5 px-4 font-bold text-blue-600">{ad.leads} leads</td>
                      <td className="py-3.5 px-4 font-sans text-xs text-emerald-600">{ad.cpl}</td>
                      <td className="py-3.5 px-4">
                        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                          {ad.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ── Connect Socials Modal ── */}
      <Dialog open={socialsOpen} onOpenChange={setSocialsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              <span>Connect Social Accounts</span>
            </DialogTitle>
            <DialogDescription>
              Connect your official Facebook, Instagram, LinkedIn, and Twitter/X accounts to schedule multi-platform posts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between p-3 rounded-xl border" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-3">
                <Instagram className="w-5 h-5 text-pink-500" />
                <div>
                  <div className="font-bold text-xs">Instagram Professional</div>
                  <div className="text-[11px] text-slate-400">Feed posts, Reels, and Carousel publishing</div>
                </div>
              </div>
              <Link to="/marketplace" onClick={() => setSocialsOpen(false)}>
                <Button size="sm" variant="outline" className="h-8 text-xs font-semibold">
                  Connect Meta
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl border" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-3">
                <Facebook className="w-5 h-5 text-blue-600" />
                <div>
                  <div className="font-bold text-xs">Facebook Business Page</div>
                  <div className="text-[11px] text-slate-400">Page updates, photo albums, and videos</div>
                </div>
              </div>
              <Link to="/marketplace" onClick={() => setSocialsOpen(false)}>
                <Button size="sm" variant="outline" className="h-8 text-xs font-semibold">
                  Connect Meta
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl border" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-3">
                <Linkedin className="w-5 h-5 text-blue-700" />
                <div>
                  <div className="font-bold text-xs">LinkedIn Company Page</div>
                  <div className="text-[11px] text-slate-400">B2B client engagement and articles</div>
                </div>
              </div>
              <Button size="sm" variant="outline" className="h-8 text-xs font-semibold" onClick={() => toast.success("LinkedIn OAuth initialized")}>
                Connect
              </Button>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl border" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-3">
                <Twitter className="w-5 h-5 text-sky-500" />
                <div>
                  <div className="font-bold text-xs">Twitter / X Profile</div>
                  <div className="text-[11px] text-slate-400">Real-time status updates and threads</div>
                </div>
              </div>
              <Button size="sm" variant="outline" className="h-8 text-xs font-semibold" onClick={() => toast.success("Twitter / X OAuth initialized")}>
                Connect
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setSocialsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
